import { forwardRef, useRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../../lib/utils';

const HEX_RGB = /^#[0-9a-fA-F]{6}$/;

export interface ColorInputProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'value' | 'onChange'
> {
  /** Current colour as `#RRGGBB`. Empty / invalid → renders the muted swatch. */
  value: string;
  /** Fired with the new colour as `#RRGGBB` (always lowercase). */
  onChange: (hex: string) => void;
  /** Highlight error state (red border). */
  invalid?: boolean;
}

/**
 * Square swatch button that opens the browser's native colour picker on
 * click / keyboard activation. We intentionally lean on `<input type="color">`
 * to inherit OS-level a11y + i18n; the button is what the eye sees.
 *
 * The hidden input is positioned over the swatch (instead of `display: none`)
 * because Safari refuses to open the picker for an off-screen input on
 * synthetic clicks.
 */
const ColorInput = forwardRef<HTMLButtonElement, ColorInputProps>(
  ({ value, onChange, invalid, className, ...props }, ref) => {
    const hiddenRef = useRef<HTMLInputElement>(null);
    const isValid = HEX_RGB.test(value);
    const swatchColor = isValid ? value : undefined;

    return (
      <button
        ref={ref}
        type="button"
        aria-label="Selecionar cor"
        onClick={() => hiddenRef.current?.click()}
        className={cn(
          'relative inline-block size-9 shrink-0 overflow-hidden rounded-md border border-input transition-shadow',
          'focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none',
          !isValid && 'bg-muted',
          invalid && 'border-destructive',
          className
        )}
        style={swatchColor ? { backgroundColor: swatchColor } : undefined}
        {...props}
      >
        <input
          ref={hiddenRef}
          type="color"
          value={isValid ? value : '#000000'}
          onChange={(event) => onChange(event.target.value.toLowerCase())}
          tabIndex={-1}
          aria-hidden
          className="absolute inset-0 size-full cursor-pointer opacity-0"
        />
      </button>
    );
  }
);
ColorInput.displayName = 'ColorInput';

export { ColorInput };
